import { Resources } from '@ragu-components/ord-resources';
import { useGetOne, useGetRecordRepresentation, useListContext } from 'react-admin';
import { useParams } from 'react-router';

import { isoToDateTime, isoToTime } from 'src/lib/timeUtils';

import type { CallSessionEvent } from '@ragu-components/ord-types';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;
type CallSessionEventType = NonNullable<CallSessionEvent['details']>['type'];
export interface ChannelTreeNode {
  channelId: string;
  parentChannelId: string | null;
  events: CallSessionEvent[];
  children: ChannelTreeNode[];
}

export interface DetailEntry {
  key: string;
  label: string;
  value: string;
}

export const MISSING_CHANNEL_ID = '__missing_channel__';
const EVENT_DETAIL_PRIORITY = ['description', 'called_account', 'mobility_username', 'to', 'digit', 'called_number'];

const SIGNIFICANT_EVENT_TYPES = new Set<CallSessionEventType>([
  'agent_call',
  'user_call',
  'outbound_call',
  'queue',
  'disconnect',
  'blind_tx',
  'attended_tx',
]);

/**
 * Checks if an event is considered significant (worth highlighting).
 */
export const isSignificantEvent = (event: CallSessionEvent): boolean => {
  const detailsRecord = toDetailsRecordInternal(event.details);
  const eventType = typeof detailsRecord?.type === 'string' ? (detailsRecord.type as CallSessionEventType) : null;
  return eventType !== null && SIGNIFICANT_EVENT_TYPES.has(eventType);
};

/**
 * Internal helper for getting details record (used before export).
 */
const toDetailsRecordInternal = (details: CallSessionEvent['details']): Record<string, unknown> | null => {
  if (details === null || details === undefined) return null;
  if (typeof details !== 'object' || Array.isArray(details)) return null;
  return details;
};

/**
 * Normalises the event details into a plain record to simplify downstream rendering.
 */
export const toDetailsRecord = (details: CallSessionEvent['details']): Record<string, unknown> | null => {
  if (details === null || details === undefined) return null;
  if (typeof details !== 'object' || Array.isArray(details)) return null;
  return details;
};

/**
 * Checks if a field key looks like a timestamp field.
 */
const isTimestampField = (key: string): boolean => {
  return key.endsWith('_time') || key.startsWith('ts_');
};

/**
 * Converts a detail value to a human readable string representation.
 * Automatically formats timestamp fields (both ISO strings and Unix timestamps) to time-only format.
 */
export const formatDetailValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'string') {
    if (isTimestampField(key)) {
      return isoToTime(value);
    }
    return value;
  }
  if (typeof value === 'number') {
    if (isTimestampField(key)) {
      // Convert Unix timestamp to ISO string, then to time-only format
      return isoToTime(new Date(value).toISOString());
    }
    return String(value);
  }
  if (typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return 'N/A';
  }
};

/**
 * Turns an underscored event type into title case for UI labels.
 */
export const formatEventTypeLabel = (value: string): string => {
  return value
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

/**
 * Picks the most meaningful detail field to highlight for an event card.
 */
export const getPreferredEventDetail = (detailsRecord: Record<string, unknown>): string | null => {
  for (const key of EVENT_DETAIL_PRIORITY) {
    const detailValue = detailsRecord[key];
    if (detailValue === undefined || detailValue === null) continue;
    if (typeof detailValue === 'string' || typeof detailValue === 'number') {
      return String(detailValue);
    }
  }
  return null;
};

/**
 * Groups session events into a channel tree, preserving chronological order.
 */
export const buildChannelTree = (events: CallSessionEvent[]): ChannelTreeNode[] => {
  const grouped = new Map<string, { channelId: string; parentChannelId: string | null; events: CallSessionEvent[] }>();

  events.forEach((event) => {
    const channelId = event.channel_id ?? MISSING_CHANNEL_ID;
    const parentChannelId = event.parent_channel_id ?? null;

    if (!grouped.has(channelId)) {
      grouped.set(channelId, { channelId, parentChannelId, events: [] });
    }

    const group = grouped.get(channelId);
    if (group === undefined) return;

    if (group.parentChannelId === null && parentChannelId !== null) {
      group.parentChannelId = parentChannelId;
    }

    group.events.push(event);
  });

  const nodes = new Map<string, ChannelTreeNode>();
  grouped.forEach((group, channelId) => {
    group.events.sort((a, b) => new Date(a.ts_event).getTime() - new Date(b.ts_event).getTime());
    nodes.set(channelId, { ...group, children: [] });
  });

  const getFirstTimestamp = (node: ChannelTreeNode): number => {
    const firstTs = node.events[0]?.ts_event;
    return firstTs !== undefined ? new Date(firstTs).getTime() : Number.POSITIVE_INFINITY;
  };

  nodes.forEach((node) => {
    if (node.parentChannelId !== null) {
      const parentNode = nodes.get(node.parentChannelId);
      if (parentNode !== undefined) {
        parentNode.children.push(node);
      }
    }
  });

  const sortBranch = (node: ChannelTreeNode): void => {
    node.children.sort((a, b) => getFirstTimestamp(a) - getFirstTimestamp(b));
    node.children.forEach(sortBranch);
  };

  const roots: ChannelTreeNode[] = [];
  nodes.forEach((node) => {
    if (node.parentChannelId === null || !nodes.has(node.parentChannelId)) {
      roots.push(node);
    }
  });

  roots.sort((a, b) => getFirstTimestamp(a) - getFirstTimestamp(b));
  roots.forEach(sortBranch);

  return roots;
};

/**
 * React-admin helper hook returning the list context with a safe default.
 */
export const useCallSessionEvents = (): {
  events: CallSessionEvent[];
  isLoading: boolean;
  error: unknown;
} => {
  const { data, isLoading, error } = useListContext<CallSessionEvent>();

  return {
    events: data ?? [],
    isLoading: isLoading === true,
    error,
  };
};

/**
 * Builds the tree structure from events. React Compiler handles memoization.
 */
export const useChannelTree = (events: CallSessionEvent[]): ChannelTreeNode[] => {
  return buildChannelTree(events);
};

export interface ChannelSummary {
  title: string;
  stats: Array<{ label: string; value: string }>;
  eventCountLabel: string;
  parentLabel: string;
  channelId: string;
  firstEventTime?: string;
  lastEventTime?: string;
}

/**
 * Summarises metadata for a channel to feed the hierarchy view.
 */
export const buildChannelSummary = (channel: ChannelTreeNode, translate: TranslateFn): ChannelSummary => {
  const displayChannelId =
    channel.channelId === MISSING_CHANNEL_ID
      ? translate('resources.call_session_events.summary.unknownChannel', { _: 'Unknown channel' })
      : channel.channelId;

  const parentLabel =
    channel.parentChannelId ?? translate('resources.call_session_events.summary.parentFallback', { _: 'Root channel' });

  const firstEvent = channel.events[0];
  const lastEvent = channel.events[channel.events.length - 1];
  const hasDistinctLastEvent =
    lastEvent !== undefined && firstEvent !== undefined && lastEvent.ts_event !== firstEvent.ts_event;

  // Count both direct events and child channels
  const itemCount = channel.events.length + channel.children.length;
  const eventCountLabel = translate('resources.call_session_events.summary.eventCount', {
    smart_count: itemCount,
    _: `${itemCount} events`,
  });
  const stats: Array<{ label: string; value: string }> = [
    { label: translate('resources.call_session_events.summary.parent', { _: 'Parent channel' }), value: parentLabel },
  ];

  if (firstEvent !== undefined) {
    stats.push({
      label: translate('resources.call_session_events.summary.firstEvent', { _: 'First event' }),
      value: isoToDateTime(firstEvent.ts_event),
    });
  }

  if (hasDistinctLastEvent) {
    stats.push({
      label: translate('resources.call_session_events.summary.lastEvent', { _: 'Last event' }),
      value: isoToDateTime(lastEvent.ts_event),
    });
  }

  return {
    title: translate('resources.call_session_events.summary.channelTitle', {
      channel: displayChannelId,
      _: `Channel ${displayChannelId}`,
    }),
    stats,
    eventCountLabel,
    parentLabel,
    channelId: displayChannelId,
    firstEventTime: firstEvent !== undefined ? isoToTime(firstEvent.ts_event) : undefined,
    lastEventTime: lastEvent !== undefined ? isoToTime(lastEvent.ts_event) : undefined,
  };
};

/**
 * Produces detail label/value pairs with translations for rendering.
 */
export const buildDetailEntries = (event: CallSessionEvent, translate: TranslateFn): DetailEntry[] => {
  const detailsRecord = toDetailsRecord(event.details);
  if (detailsRecord === null) return [];

  const isServiceFailure = detailsRecord.type === 'service_failure';

  return Object.entries(detailsRecord)
    .filter(([key]) => key !== 'type')
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => ({
      key,
      label: translate(`resources.call_session_events.fields.details.${key}`, { _: key.replace(/_/g, ' ') }),
      value:
        isServiceFailure && (key === 'feature' || key === 'reason') && typeof value === 'string'
          ? translate(`resources.call_session_events.serviceFailure.${key}.${value}`, { _: value })
          : formatDetailValue(key, value),
    }));
};

export interface FakeVoiceDetectionSummary {
  /** Verdict sentence, e.g. "Synthetic voice detected" */
  label: string;
  /** Synthetic likelihood as a rounded percentage, e.g. "80% synthetic"; null when the score is missing */
  score: string | null;
  /** Palette key to colour the verdict with */
  color: 'error' | 'success';
}

/**
 * Renders a fake voice detection event as a verdict sentence with a synthetic likelihood percentage.
 * The confidence score is always "how synthetic", regardless of the prediction, so it is shown as such.
 * Returns null for any other event type.
 */
export const getFakeVoiceDetectionSummary = (
  event: CallSessionEvent,
  translate: TranslateFn
): FakeVoiceDetectionSummary | null => {
  const detailsRecord = toDetailsRecord(event.details);
  if (detailsRecord?.type !== 'fake_voice_detection') return null;

  const isSynthetic = detailsRecord.prediction === 'synthetic';
  const rawScore = detailsRecord.confidence_score;
  const score =
    typeof rawScore === 'number' && Number.isFinite(rawScore)
      ? translate('resources.call_session_events.fakeVoiceDetection.score', {
          percentage: Math.round(rawScore * 100),
          _: `${Math.round(rawScore * 100)}% synthetic`,
        })
      : null;

  return {
    label: isSynthetic
      ? translate('resources.call_session_events.fakeVoiceDetection.synthetic', { _: 'Synthetic voice detected' })
      : translate('resources.call_session_events.fakeVoiceDetection.real', { _: 'Real voice detected' }),
    score,
    color: isSynthetic ? 'error' : 'success',
  };
};

/**
 * Filters out detail entries that duplicate values already displayed elsewhere.
 */
export const filterDuplicateEntries = (entries: DetailEntry[], displayedDetail: string | null): DetailEntry[] => {
  return displayedDetail !== null ? entries.filter((entry) => entry.value !== displayedDetail) : entries;
};

/**
 * Derives a display label and optional detail for an event type.
 */
export const getEventTypeLabel = (
  event: CallSessionEvent,
  translate: TranslateFn
): { label: string; detail: string | null } => {
  const detailsRecord = toDetailsRecord(event.details);
  const eventTypeCandidate = detailsRecord?.type;
  const label =
    typeof eventTypeCandidate === 'string'
      ? translate(`resources.call_session_events.types.${eventTypeCandidate}`, {
          _: formatEventTypeLabel(eventTypeCandidate),
        })
      : translate('resources.call_session_events.event.unknownType', { _: 'Unknown type' });
  const detail = detailsRecord !== null ? getPreferredEventDetail(detailsRecord) : null;
  return { label, detail };
};

export const useCallSessionEventTitle = (defaultTitle: string): string => {
  const { id } = useParams<'id'>();
  const { data: callSession, isLoading } = useGetOne(Resources.call_sessions, { id });
  const getRecordRepresentation = useGetRecordRepresentation(Resources.call_sessions);

  if (isLoading || callSession === undefined) {
    return defaultTitle;
  } else {
    const recordTitle = getRecordRepresentation(callSession);
    return typeof recordTitle === 'string' ? recordTitle : defaultTitle;
  }
};
